import React, { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  User,
  Building,
  Users,
  Globe,
  ArrowRight,
} from "lucide-react";
import Footer from "../components/Footer";
import StarRating from "../components/StarRating";
import { interiorTestimonials } from "../data/testimonials";
import AOS from "aos";
import "aos/dist/aos.css";
import Contact from "../components/contact";

const InteriorDesign = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentProject, setCurrentProject] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleElements, setVisibleElements] = useState(new Set());

  // Track the active image index for the currently selected project's thumbnail view
  const [currentProjectImageIndex, setCurrentProjectImageIndex] = useState(0);

  const observerRef = useRef(null);

  // HERO IMAGES
  const heroImages = [
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779699437/DSC_1590-HDR_2_1_bpgvyo.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779699438/Reception_1_-_Copy_rzvcks.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779699436/Bharil_Ji_living_area_1_d7fk95.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779699241/SHEET_NO_1043FIRST_FLOOR_LIVING_VIEW_1_xywwyx.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779699241/Copy_of_AMP-Valley_Grassfield_Resort-4_mgucix.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779699240/Copy_of_AMP-Valley_Grassfield_Resort-173_gmmzzk.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779699239/img26_1_rucxc1.jpg",
  ];

  const milestones = [
    { icon: Building, number: "500+", label: "Interiors" },
    { icon: User, number: "20", label: "Years Experience" },
    { icon: Users, number: "100+", label: "Happy Clients" },
    { icon: Globe, number: "Premium", label: "Quality" },
  ];

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: "ease-out",
      offset: 100,
    });
  }, []);

  useEffect(() => {
    AOS.refresh();
  }, [showFullDescription]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements(
              (prev) => new Set([...prev, entry.target.dataset.animateId])
            );
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -100px 0px" }
    );

    const elements = document.querySelectorAll("[data-animate-id]");
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading]);

  // Fetch interior projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(
          "https://archpoint.onrender.com/api/interior-images"
        );
        const data = await response.json();
        setProjects(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // Reset image preview index whenever the main project changes
  useEffect(() => {
    setCurrentProjectImageIndex(0);
  }, [currentProject]);

  // Auto-slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  const prevSlide = () =>
    setCurrentSlide(
      (prev) => (prev - 1 + heroImages.length) % heroImages.length
    );
  const nextProject = () =>
    setCurrentProject((prev) => (prev + 1) % projects.length);
  const prevProject = () =>
    setCurrentProject((prev) => (prev - 1 + projects.length) % projects.length);

  const getAnimationClass = (id, animation = "fade-up", delay = 0) => {
    const isVisible = visibleElements.has(id);
    const baseClass = "transition-all duration-1000";
    const delayClass = delay > 0 ? `delay-${delay}` : "";

    if (!isVisible) {
      switch (animation) {
        case "fade-up":
          return `${baseClass} opacity-0 translate-y-12`;
        case "fade-right":
          return `${baseClass} opacity-0 -translate-x-12`;
        case "fade-left":
          return `${baseClass} opacity-0 translate-x-12`;
        case "zoom-in":
          return `${baseClass} opacity-0 scale-95`;
        default:
          return `${baseClass} opacity-0`;
      }
    }
    return `${baseClass} opacity-100 translate-y-0 translate-x-0 scale-100 ${delayClass}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        .delay-100 { transition-delay: 100ms; }
        .delay-200 { transition-delay: 200ms; }
        .delay-300 { transition-delay: 300ms; }
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>

      {/* HERO CAROUSEL */}
      <section className="relative h-screen overflow-hidden">
        <div className="relative w-full h-full">
          {heroImages.map((imageUrl, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={imageUrl}
                alt={`Interior Hero ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            </div>
          ))}
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white text-center tracking-wide">
            <span className="text-yellow-400">Interior</span>
            <br />
            Design
          </h1>
        </div>

        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-yellow-400 transition-colors duration-300 z-10"
        >
          <ChevronLeft size={48} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-yellow-400 transition-colors duration-300 z-10"
        >
          <ChevronRight size={48} />
        </button>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                index === currentSlide
                  ? "bg-yellow-400"
                  : "bg-white bg-opacity-50"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ---------------- OUR SERVICES ---------------- */}
      <section className="py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div data-aos="fade-right">
              <h2 className="text-4xl font-bold mb-6 text-gray-800">
                Our <span className="text-yellow-500">Services</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We provide complete interior design services to make your space
                remarkable.
              </p>
              {showFullDescription && (
                <div
                  className="text-gray-600 leading-relaxed mb-6"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  <p className="mb-4">
                    At Arch Point, we bring together deep expertise and a passion
                    for creativity to deliver innovative interior design
                    solutions tailored to each client's lifestyle. Our team of
                    best-in-class designers works closely with you to understand
                    your vision, preferences, and functional needs—transforming
                    ideas into inspiring, livable spaces.
                  </p>
                  <p className="mb-4">
                    From concept to completion, we ensure every detail reflects
                    your unique style, turning your dream home into a beautifully
                    designed reality.
                  </p>
                </div>
              )}
              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="inline-flex items-center px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors duration-300 font-semibold"
              >
                {showFullDescription ? "Read Less" : "Read More"}
                <ArrowRight className="ml-2" size={20} />
              </button>
            </div>
            <div data-aos="fade-left" data-aos-delay="200">
              <Contact />
            </div>
          </div>
        </div>
      </section>

      {/* Our USP Section */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div data-aos="fade-right">
              <h2 className="text-4xl font-bold mb-6 text-gray-800">
                Our <span className="text-yellow-500">USP</span>
              </h2>
              <h3 className="text-2xl font-semibold mb-4 text-gray-700">
                Lifestyle-Centered Design
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our interior design philosophy centers on understanding your
                lifestyle and creating spaces that not only look beautiful but
                enhance your daily living experience. We blend aesthetics with
                functionality, ensuring every element serves a purpose while
                reflecting your personal style.
              </p>
            </div>
            <div className="relative" data-aos="fade-left" data-aos-delay="200">
              <img
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop"
                alt="Modern Interior Design"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-yellow-500 to-transparent opacity-20 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- MILESTONES ---------------- */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-yellow-500">
        <div className="max-w-7xl mx-auto">
          <h2
            data-animate-id="milestones-title"
            className={`text-4xl font-extrabold text-center mb-12 text-white ${getAnimationClass(
              "milestones-title",
              "fade-up"
            )}`}
          >
            Our Milestones
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => {
              const IconComponent = milestone.icon;
              return (
                <div
                  key={index}
                  data-animate-id={`milestone-${index}`}
                  className={`text-center ${getAnimationClass(
                    `milestone-${index}`,
                    "zoom-in",
                    index * 100
                  )}`}
                >
                  <div className="bg-white bg-opacity-20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-4xl font-extrabold text-white mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]">
                    {milestone.number}
                  </h3>
                  <p className="text-white font-bold tracking-wide">
                    {milestone.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      {projects.length > 0 && (
        <section className="py-20 px-4 md:px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 text-gray-800">
              Interior <span className="text-yellow-500">Projects</span>
            </h2>
            <div className="relative mb-8">
              <img
                src={
                  projects[currentProject].images[currentProjectImageIndex] ||
                  projects[currentProject].images[0]
                }
                alt={projects[currentProject].name}
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl transition-all duration-300"
              />
              <div className="absolute inset-0 bg-black/30 rounded-2xl pointer-events-none"></div>
              <div className="absolute bottom-6 left-6 pointer-events-none">
                <h3 className="text-3xl font-bold text-white">
                  {projects[currentProject].name}
                </h3>
              </div>
              <button
                onClick={prevProject}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-yellow-400 bg-black/40 hover:bg-black/60 p-2 rounded-full transition"
              >
                <ChevronLeft size={40} />
              </button>
              <button
                onClick={nextProject}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-yellow-400 bg-black/40 hover:bg-black/60 p-2 rounded-full transition"
              >
                <ChevronRight size={40} />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {projects[currentProject].images.slice(0, 8).map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCurrentProjectImageIndex(i)}
                  className={`relative overflow-hidden rounded-lg shadow-md transition transform hover:-translate-y-1 focus:outline-none ${
                    currentProjectImageIndex === i
                      ? "ring-4 ring-yellow-500"
                      : "opacity-80 hover:opacity-100"
                  }`}
                >
                  <img
                    src={img}
                    alt={`project-${i}`}
                    className="w-full h-32 object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2
            data-animate-id="testimonials-title"
            className={`text-4xl font-bold text-center mb-12 text-gray-800 ${getAnimationClass(
              "testimonials-title",
              "fade-up"
            )}`}
          >
            What Our <span className="text-yellow-500">Clients Say</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {interiorTestimonials.map((testimonial, index) => (
              <div
                key={index}
                data-animate-id={`testimonial-${index}`}
                className={`bg-white rounded-2xl p-8 shadow-xl ${getAnimationClass(
                  `testimonial-${index}`,
                  "fade-up",
                  index * 100
                )}`}
              >
                <StarRating rating={testimonial.rating} />
                <p className="text-gray-600 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {testimonial.name}
                    </h4>
                    <p className="text-gray-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InteriorDesign;